import React from 'react';
import {
    Timeline,
    TimelineContent,
    TimelineDot,
} from '@mui/lab';
import { styled, Typography, Box } from '@mui/material';
import Lottie from 'react-lottie';
import RedBlinkingLottie from './Lotties/RedBlinkingLottie.json';

const StyledTimeline = styled(Timeline)`
    padding: 8px 0;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    width: 100%;
    margin: 0;
    padding: 0;
    position: relative;
    & .MuiTimelineItem-root {
        min-height: auto;
        padding: 0;
        margin: 0;
        flex: 1;
    }
    & .MuiTimelineItem-root:before {
        display: none;
    }
    &:before {
        content: '';
        position: absolute;
        top: 16px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #E86C68;
        z-index: 0;
    }
`;

const StyledTimelineContent = styled(TimelineContent)`
    padding: 0px;
    margin: 0;
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 36px;
`;

const StyledTimelineDot = styled(TimelineDot)`
    margin: 0;
    padding: 0;
    width: 10px; /* actual width 14px: 12px + 2px border each side */
    height: 10px; /* actual height 14px: 12px + 2px border each side */
    background-color: ${props => props.color ? props.color : '#E86C68'};
    border: 2px solid ${props => props.color ? props.color : '#E86C68'};
    border-radius: ${props => props.variant === 'square' ? '2px' : '50%'};
    position: absolute;
    top: 10px;
    left: ${props =>
        props.position === 100
            ? `calc(${props.position}% - 14px)` /* 14px is the width of the timeline dot */
            : `${props.position}%`};
    box-shadow: none;
`;

const StyledTimelineAnimate = styled(StyledTimelineDot)`
    background-color: transparent;
    border: none;
    width: auto;
    height: auto;
    top: 4px;
`;

const DateText = styled(Typography)`
    color: ${props => props.theme.palette.text.secondary};
    font-size: 0.875rem;
    margin-top: 8px;
`;

const SeparatorText = styled(Typography)`
    color: ${props => props.theme.palette.text.primary};
    font-size: 1rem;
    margin-top: 8px;
`;

const StartEndDate = styled(Typography)`
    color: #494949;
    font-weight: 400;
    font-size: 12px;
    line-height: 20px;
`;

const TimelineWrapper = styled(Box)`
    width: 100%;
    position: relative;
    margin-top: 4px;
    margin-bottom: 4px;
    padding-right: 20px;
`;

const SeparatorContent = styled(StyledTimelineContent)`
    position: absolute;
    left: ${props => props.position}%;
`;

const TimelineItemWrapper = styled(Box)`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    position: relative;
    width: 100%;
`;

function calculatePosition(date, startDate, endDate) {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const current = new Date(date).getTime();

    const totalDuration = end - start;
    const currentPosition = current - start;

    return (currentPosition / totalDuration) * 100;
}

function HorizontalTimeline({ data }) {
    const { start, timelineSeparators, end } = data;

    return (
        <TimelineWrapper>
            <StyledTimeline>
                {/* Start Date */}
                <TimelineItemWrapper>
                    <StyledTimelineDot
                        variant="square"
                        position={0}
                    />
                    <StyledTimelineContent>
                        <Typography variant="fw400s14c494949" sx={{ lineHeight: '20px' }}>{start.label}</Typography>
                        <StartEndDate>
                            {start.subLabel}
                        </StartEndDate>
                    </StyledTimelineContent>
                </TimelineItemWrapper>

                {/* Timeline Separators */}
                {timelineSeparators.map((separator, index) => {
                    const position = calculatePosition(separator.date, start.date, end.date);
                    return (
                        <TimelineItemWrapper key={index}>
                            <StyledTimelineDot position={position} />
                            <SeparatorContent position={position}>
                                <Typography variant="fw400s14c494949" sx={{ lineHeight: '20px' }}>{separator.label}</Typography>
                                <StartEndDate>
                                    {separator.subLabel}
                                </StartEndDate>
                            </SeparatorContent>
                        </TimelineItemWrapper>
                    );
                })}

                {/* End Date */}
                <TimelineItemWrapper sx={{ alignItems: 'flex-end' }}>
                    {end.label.toLowerCase() === 'ongoing' ? (
                        <StyledTimelineAnimate position={100}>
                            <Lottie
                                options={{
                                    loop: true,
                                    autoplay: true,
                                    animationData: RedBlinkingLottie,
                                    rendererSettings: {
                                        preserveAspectRatio: "xMidYMid slice"
                                    }
                                }}
                                height={26}
                                width={26}
                            />
                        </StyledTimelineAnimate>
                    ) : (
                        <StyledTimelineDot
                            color={end.label.toLowerCase() == 'expired' ? '#A2A2A2' : "#8BC993"}
                            variant="square"
                            position={100}
                        />
                    )}
                    <SeparatorContent sx={{ alignItems: 'flex-end' }}>
                        <Typography variant="fw400s14c494949" sx={{ lineHeight: '20px' }}>{end.label}</Typography>
                        <StartEndDate>
                            {end.subLabel}
                        </StartEndDate>
                    </SeparatorContent>
                </TimelineItemWrapper>
            </StyledTimeline>
        </TimelineWrapper>
    );
}

export default HorizontalTimeline;
